import { motion } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { SITE, MEDIA, HERO } from "@/lib/site";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-brand-accent/10 blur-3xl" />
        <div className="absolute top-1/3 -right-40 h-[420px] w-[420px] rounded-full bg-brand-secondary/10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-brand-accent font-semibold mb-6"
          >
            {SITE.brand} · {SITE.overline}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-brand-primary"
          >
            The Crown
            <br />
            Jewel of Indian
            <br />
            <span className="italic text-brand-accent">Orchards.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-brand-textMuted leading-relaxed"
          >
            {SITE.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={SITE.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-brand-base px-8 py-4 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
            >
              <MessageCircle size={18} strokeWidth={1.8} />
              Order on WhatsApp
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href="#varieties"
              data-testid="hero-explore-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-primary/30 hover:border-brand-primary text-brand-primary px-8 py-4 text-sm font-medium transition-all duration-300"
            >
              Explore varieties
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-brand-borderColor pt-8"
          >
            {HERO.stats.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label}`}>
                <div className="font-display text-2xl sm:text-3xl text-brand-primary">
                  {s.value}
                </div>
                <div className="text-xs uppercase tracking-wider text-brand-textMuted mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-brand-borderColor bg-brand-surface"
          >
            <img
              src={MEDIA.heroBasket}
              alt="Premium Indian mangoes in a handcrafted basket"
              className="h-full w-full object-cover"
            />
            <div className="absolute bottom-5 left-5 right-5 bg-[#FDFBF7]/85 backdrop-blur-md rounded-xl px-5 py-4 border border-white/60">
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-accent font-semibold">
                {HERO.season}
              </div>
              <div className="font-display text-lg text-brand-primary mt-1">
                {HERO.seasonLabel}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="hidden lg:block absolute -top-8 -left-12 w-32 h-32 rounded-2xl overflow-hidden border border-brand-borderColor animate-float shadow-sm"
          >
            <img
              src={MEDIA.slice}
              alt="Mango slice detail"
              className="h-full w-full object-cover"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
